import React, { useCallback, useId, useMemo, useState } from 'react';
import { App, Button, Input, Select, Space, Typography } from 'antd';
import { CopyOutlined } from '@ant-design/icons';

import useCollectionAccess from '../../hooks/useCollectionAccess';
import { canManage } from '../../utils/permissions';
import { copyTextToClipboard } from '../../utils/clipboard';
import { formatDisplayDate } from '../../utils/dateDisplay';
import { buildShortLinkUrl } from '../../utils/shareUrls';
import { queryUsesMe } from '../../utils/kqlBuilder';
import { negatedTagWarningText } from '../../utils/tags';
import IncludeFilesField from './IncludeFilesField';
import {
	createShareLink,
	describeShareLinkError,
	kindTakesFiles,
	DEFAULT_EXPIRY_DAYS,
	EXPIRY_OPTIONS,
	ONE_TIME_CODE_NOTE,
} from '../../services/shareLinks';
import { LINK_PICK_HINT, elementRefusalOfError, elementRefusalText, isPickableElementRefusal } from '../../utils/elementRefusal';

const { Text } = Typography;

/**
 * "Temporary public link" — the one place a temporary share link is minted
 * outside the Sharing tab. It rides inside the generic ShareModal's `extra`
 * slot, so every share surface (item, collection, items view, card) offers
 * the same control over the same target it is already sharing.
 *
 * The two halves of a share modal are NOT the same thing, and the section
 * says so: the direct link above needs an account and the collection's
 * permissions; this one needs neither, until it expires. That asymmetry is
 * why the section is manage-gated — handing out account-free access is a
 * manager's decision, never a reader's — and why it FAILS CLOSED: while the
 * collection record is still loading there is no myAccess to read, so
 * nothing renders. Non-managers see the share modal exactly as before.
 *
 * The code comes back exactly once, in the create response, so a successful
 * create swaps the form for a one-time reveal rather than adding a row.
 *
 * Props:
 *   collectionId  the collection the link is scoped to (required)
 *   collection    the record carrying myAccess, when the surface has one
 *   kind          'view' | 'item' | 'card'
 *   itemId/cardId the target, for kinds 'item'/'card'
 *   query         the stored filter, for kind 'view' (optional — no query
 *                 means the whole collection)
 *   scopeOptions  [{value, label, query}] — when given, a Scope select whose
 *                 choice picks the query. Used by the items-view share.
 *   blockedReason when set, the section explains why no link can be made
 *                 (a filter that cannot be read) instead of
 *                 offering the form.
 *   caveat        a muted line above the form for what the link will NOT
 *                 carry, when the surface knows of something.
 */
// The API's sentence for a query naming the caller (keepr-api
// utils/shareLinkUtils.js QUERY_ME): a public page has no signed-in reader.
export const QUERY_ME_UNPUBLISHABLE = 'A view link cannot filter on "me": a public page has no signed-in reader.';

export default function TempShareLinkSection({
	collectionId,
	collection = null,
	kind = 'view',
	itemId,
	cardId,
	query = '',
	scopeOptions = null,
	blockedReason = null,
	caveat = null,
	className = '',
}) {
	const { message } = App.useApp();
	const access = useCollectionAccess();
	// Two share modals can be mounted at once (a modal closing behind the one
	// opening), so the field ids have to be per-instance or the labels point
	// at the wrong inputs while both are in the DOM.
	const fieldId = useId();

	const [name, setName] = useState('');
	const [expiresInDays, setExpiresInDays] = useState(DEFAULT_EXPIRY_DAYS);
	const [scope, setScope] = useState(scopeOptions?.[0]?.value);
	// "Include files" (PR 10c): a view or item link only, on by default.
	const [includeFiles, setIncludeFiles] = useState(true);
	const [creating, setCreating] = useState(false);
	// The one-time reveal: {code, url, name, expiresAt}.
	const [created, setCreated] = useState(null);

	// Prefer whichever record actually carries the server's myAccess
	// annotation — a manager must not be gated out because this surface holds
	// a stripped copy of the collection. Both sources are server-computed, so
	// reading either can never widen access beyond what the server granted.
	const canCreate = canManage(collection) || access.canManage;

	const activeScope = useMemo(
		() => (scopeOptions || []).find(option => option.value === scope) || null,
		[scopeOptions, scope]
	);
	// A view query naming "me" cannot be published (400 query_me_unpublishable):
	// the section refuses it here, in the API's words, rather than after the
	// round trip. Only a view has a query to check.
	const effectiveQuery = activeScope ? activeScope.query : query;
	const meBlocked = kind === 'view' && queryUsesMe(effectiveQuery);
	const blocked = blockedReason || (meBlocked ? QUERY_ME_UNPUBLISHABLE : null);

	const handleCreate = useCallback(async () => {
		if (!collectionId) return;
		setCreating(true);
		try {
			const link = await createShareLink(collectionId, {
				kind,
				item_id: itemId,
				card_id: cardId,
				query: activeScope ? activeScope.query : query,
				name: name.trim() || undefined,
				expiresInDays,
				includeFiles: kindTakesFiles(kind) ? includeFiles : undefined,
			});
			// `url` is null when the API has no APP_BASE_URL configured; the
			// code always comes back, so this origin can build the link.
			setCreated({ ...link, url: link?.url || buildShortLinkUrl(link?.code) });
			// A view's filter that leaves items out by a restricted tag (T7,
			// `negated_restricted_tag`): the API's warning, as given.
			const warning = negatedTagWarningText(link?.warnings);
			if (warning) message.warning(warning);
		} catch (error) {
			// The list's filter, refused for an element it could mean several
			// of: a public link decides who sees what and is read in every
			// sub-collection too, so the list may not have asked. Say what the
			// name could be, and where to answer it: pick the element in
			// + Filter, then make the link again.
			const element = elementRefusalOfError(error);
			message.error(element
				? (isPickableElementRefusal(element) ? `${elementRefusalText(element)} ${LINK_PICK_HINT}` : elementRefusalText(element))
				: describeShareLinkError(error));
		} finally {
			setCreating(false);
		}
	}, [collectionId, kind, itemId, cardId, activeScope, query, name, expiresInDays, includeFiles, message]);

	const handleCopy = useCallback(async () => {
		if (await copyTextToClipboard(created?.url)) message.success('Link copied to clipboard');
		else message.error('Unable to copy link');
	}, [created, message]);

	const handleAnother = useCallback(() => {
		setCreated(null);
		setName('');
		setExpiresInDays(DEFAULT_EXPIRY_DAYS);
		setIncludeFiles(true);
	}, []);

	if (!canCreate || !collectionId) return null;

	const classes = ['item-share-modal__section', 'temp-share-link', className]
		.filter(Boolean).join(' ').trim();

	return (
		<div className={classes}>
			<div className="item-share-modal__section-title">Temporary public link</div>
			<Text type="secondary" className="temp-share-link__hint">
				Anyone with this link can view until it expires — no account needed.
			</Text>

			{blocked ? (
				<p className="temp-share-link__blocked">{blocked}</p>
			) : created ? (
				<>
					<p className="temp-share-link__note">{ONE_TIME_CODE_NOTE}</p>
					{created.expiresAt ? (
						<p className="temp-share-link__expires">
							Expires {formatDisplayDate(created.expiresAt)}
						</p>
					) : null}
					<Space.Compact block>
						<Input value={created.url || ''} readOnly aria-label="Temporary public link" />
						<Button type="primary" icon={<CopyOutlined />} onClick={handleCopy}>
							Copy
						</Button>
					</Space.Compact>
					<Button type="link" size="small" onClick={handleAnother}>
						Create another
					</Button>
				</>
			) : (
				<>
					{caveat ? <p className="temp-share-link__caveat">{caveat}</p> : null}

					<label className="temp-share-link__label" htmlFor={`${fieldId}-name`}>
						Name (optional)
					</label>
					<Input
						id={`${fieldId}-name`}
						maxLength={100}
						value={name}
						placeholder="Label for this link"
						onChange={event => setName(event.target.value)}
					/>

					{scopeOptions?.length ? (
						<>
							<label className="temp-share-link__label" htmlFor={`${fieldId}-scope`}>
								Scope
							</label>
							<Select
								id={`${fieldId}-scope`}
								className="temp-share-link__select"
								value={scope}
								options={scopeOptions.map(({ value, label }) => ({ value, label }))}
								onChange={setScope}
							/>
						</>
					) : null}

					<label className="temp-share-link__label" htmlFor={`${fieldId}-expiry`}>
						Expires after
					</label>
					<Select
						id={`${fieldId}-expiry`}
						className="temp-share-link__select"
						value={expiresInDays}
						options={EXPIRY_OPTIONS}
						onChange={setExpiresInDays}
					/>

					{/* A card link carries no items, so never files. */}
					{kindTakesFiles(kind) ? (
						<IncludeFilesField
							className="temp-share-link__files"
							collectionId={collectionId}
							kind={kind}
							query={effectiveQuery}
							itemId={itemId}
							checked={includeFiles}
							onChange={setIncludeFiles}
						/>
					) : null}

					<Button
						type="primary"
						block
						loading={creating}
						className="temp-share-link__create"
						onClick={handleCreate}
					>
						Create share link
					</Button>
				</>
			)}
		</div>
	);
}
